import { faMoneyBill } from '@fortawesome/pro-regular-svg-icons/faMoneyBill';
import { AppointmentListItem } from '@spike/appointments-list-model';
import clsx from 'clsx';
import ListPetInfo from 'components/Bookings2/UI/ListPetInfo/ListPetInfo';
import StatusButton from 'components/OrderDetail/StatusButton';
import Checkbox from 'components/UI/V3/Forms/Checkbox';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import ThreeDotsDropdown from 'components/UI/V3/ThreeDotsDropdown/ThreeDotsDropdown';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { FunctionComponent, MouseEvent } from 'react';
import { useMediaQuery } from 'react-responsive';
import NotesTableRow from './NotesTableRow';
import PaidIcon from './PaidIcon';
import DepositPendingIcon from './DepositPendingIcon';
import PetParent from './PetParent';
import Service from './Service';
import Staff from './Staff';
import styles from './WalkingTableRow.module.scss';
import { usePermissions } from 'hooks';

interface WalkingTableRowProps {
    app: AppointmentListItem;
    isLast?: boolean;
    scrollerContainerId: string;
    className?: string;
    onCheckout?: () => void;
    onShowOrder?: () => void;
    onShowClient?: () => void;
    onShowPet?: () => void;
    onStaffSaved?: () => void;
    selected?: boolean;
    onToggleSelect?: (checked: boolean) => void;
}

export const WalkingTableRow: FunctionComponent<WalkingTableRowProps> = props => {
    const { isUserLoggedInMarketplace } = useOrganizationMarketplaces();
    const allowActions = isUserLoggedInMarketplace(props.app.marketplaceId);

    const permissions = usePermissions();
    const isCheckoutAllowed = permissions.checkout.takePayments;

    const isMobile = useMediaQuery({ query: '(max-width:600px)' });

    const hasNotes = props.app.notes.trim().length > 0;

    const checkoutOption = {
        id: 'checkout',
        label: 'Checkout',
        icon: faMoneyBill,
        onClick: () => props.onCheckout?.()
    };

    const options = isCheckoutAllowed ? [checkoutOption] : [];

    const clickHandler = (event: MouseEvent) => {
        props.onShowOrder?.();
    };

    const petInfo = (
        <ListPetInfo
            orderId={props.app.orderId}
            pet={props.app.pet}
            onClick={allowActions ? props.onShowPet : undefined}
        />
    );

    const service = (
        <Service
            service={props.app.service}
            petId={props.app.pet.id}
            marketplaceId={props.app.marketplaceId}
            inHome={(props.app.walking?.type as string) === 'in_home'}
        />
    );

    const staff = (
        <Staff
            appointmentId={props.app.appointmentId}
            appointmentStatusId={props.app.status.id}
            member={props.app.staff}
            editable={allowActions}
            scrollerContainerId={props.scrollerContainerId}
            onStaffSaved={props.onStaffSaved}
        />
    );

    const statusButton = (
        <StatusButton
            appointmentId={props.app.appointmentId}
            apppointmentMarketplaceId={props.app.marketplaceId}
            appointmentStatus={props.app.status}
            appointmentClosedAt={props.app.closedAt ?? undefined}
            appointmentCancelationReasonId={props.app.cancellationReasonId ?? null}
            appointmentTrainingType={props.app.training?.type}
            appointmentWalkingType={props.app.walking?.type}
            recurringUuid={props.app.recurringUuid ?? undefined}
            customerFirstName={props.app.customer.firstName}
            customerLastName={props.app.customer.lastName}
            reloadOrderAfterStatusChange={false}
            businessAreaId={props.app.service.businessArea}
            fullWidth={isMobile}
            scrollerContainerId={props.scrollerContainerId}
            position="fixed"
        />
    );

    const showCheckbox = props.onToggleSelect !== undefined;
    const columnCount = showCheckbox ? 9 : 8;

    const notMobileRow = (
        <>
            <TableRow
                className={clsx(styles.row, { [styles.withoutBorder]: hasNotes }, props.className)}
                onClick={clickHandler}
            >
                {showCheckbox && (
                    <TableCell
                        className={styles.checkboxCell}
                        onClick={event => event.stopPropagation()}
                    >
                        <Checkbox
                            checked={props.selected}
                            onChange={props.onToggleSelect}
                        />
                    </TableCell>
                )}
                <TableCell className={styles.clientNameCell}>
                    <PetParent
                        customer={props.app.customer}
                        marketplaceId={props.app.marketplaceId}
                        showOnly={!allowActions}
                        showTagsIcon={!isMobile}
                        onClick={props.onShowClient}
                    />
                </TableCell>
                <TableCell>{petInfo}</TableCell>
                <TableCell>{service}</TableCell>
                <TableCell className={styles.staffCell}>{staff}</TableCell>
                <TableCell>{props.app.startAt.format('MMM DD, YYYY [at] h:mm a')}</TableCell>
                <TableCell className={styles.priceCell}>
                    ${Number(props.app.total).toFixed(2)}
                    <DepositPendingIcon show={props.app.bookingDepositPending} />
                    <PaidIcon paid={props.app.paid} />
                </TableCell>
                <TableCell
                    width={180}
                    className={styles.statusCell}
                >
                    {statusButton}
                </TableCell>
                <TableCell
                    isActions
                    onClick={event => event.stopPropagation()}
                >
                    {allowActions && (
                        <ThreeDotsDropdown
                            options={options}
                            direction={props.isLast ? 'up' : 'down'}
                        />
                    )}
                </TableCell>
            </TableRow>
            {hasNotes && (
                <NotesTableRow
                    notes={props.app.notes}
                    colSpan={columnCount}
                />
            )}
        </>
    );

    const mobileRow = (
        <>
            <TableRow
                className={clsx(styles.mobileRow, props.className)}
                onClick={clickHandler}
            >
                <TableCell className={styles.mobileCell}>
                    <div className={styles.mobileHeader}>
                        {showCheckbox && (
                            <div
                                className={styles.mobileCheckbox}
                                onClick={event => event.stopPropagation()}
                            >
                                <Checkbox
                                    checked={props.selected}
                                    onChange={props.onToggleSelect}
                                />
                            </div>
                        )}
                        <div className={styles.mobilePetContainer}>{petInfo}</div>
                        <div className={styles.mobileDateTimeContainer}>
                            <span className={styles.mobileDateTime}>{props.app.startAt.format('MMM DD, h:mm a')}</span>
                        </div>
                    </div>
                    <div className={styles.mobileBody}>
                        {service}
                        {staff}
                    </div>
                    <div className={styles.mobileFooter}>
                        {hasNotes && (
                            <NotesTableRow
                                notes={props.app.notes}
                                colSpan={8}
                            />
                        )}
                        {statusButton}
                    </div>
                </TableCell>
            </TableRow>
        </>
    );

    return isMobile ? mobileRow : notMobileRow;
};

export default WalkingTableRow;
